/**
 * AFS grain-size (sieve) SHEET — the full branded worksheet the VSync
 * GrainSizeController.php (analysis + emitXlsx) produces, ported client-side.
 *
 * gfn.ts already gives the core number (GFN = Σ(Mᵢ·massᵢ)/Σmassᵢ, AFS 1106).
 * A sieve *sheet* is the rest of the lab form around it: per-mesh % retained,
 * the product (%ret × multiplier), the running cumulative %, and the % passing,
 * with the ASTM/BSS mesh designations down the side and a Grain Fineness Number
 * at the bottom. This module is pure (no DOM, no fetch) so it stays testable;
 * SieveReportPanel does the picking / entry / rendering and sieve-export.ts the
 * XLSX/PDF. Parity target: GrainSizeController.php:113-210.
 *
 * The GFN itself is delegated to computeGFN (lib/qc/gfn.ts) — the algebra is
 * identical to VSync's Σ(%retᵢ·multᵢ)/Σ%retᵢ, since %ret = mass/Σmass·100 makes
 * the ·100 cancel top and bottom.
 */

import { AFS_US_SERIES, computeGFN, type SieveStep } from "@/lib/qc/gfn";

/** ASTM (US) + BSS (British) mesh number for one sieve. */
export interface MeshDesignations {
  astm: string;
  bss: string;
}

/**
 * Sieve aperture (µm) → ASTM / BSS mesh numbers — the mapping the VSync
 * GrainSizeController reads from its `sieve_details` table (`sieve_size`,
 * `sieve_astm`, `sieve_bss`). These are the standard AFS / BS-410 foundry-sand
 * sieve equivalences; aperture 0 is the pan. Keyed by the SAME apertures the
 * AFS US-series set (gfn.ts AFS_US_SERIES / packs/foundry/sieve-sets) uses so a
 * lookup by `step.apertureMicron` always resolves.
 */
export const MESH_BY_APERTURE: Record<number, MeshDesignations> = {
  3350: { astm: "6", bss: "5" },
  1700: { astm: "12", bss: "10" },
  850: { astm: "20", bss: "18" },
  600: { astm: "30", bss: "25" },
  425: { astm: "40", bss: "36" },
  300: { astm: "50", bss: "52" },
  212: { astm: "70", bss: "72" },
  150: { astm: "100", bss: "100" },
  106: { astm: "140", bss: "150" },
  75: { astm: "200", bss: "200" },
  53: { astm: "270", bss: "300" },
  0: { astm: "PAN", bss: "PAN" },
};

/** One row of the sieve sheet — a mesh and everything derived for it. */
export interface SieveSheetRow {
  /** The set's mesh designation (== ASTM mesh number for the AFS US series). */
  meshDesignation: string;
  astm: string;
  bss: string;
  apertureMicron: number;
  /** Aperture in mm (aperture/1000); 0 for the pan. */
  screenMm: number;
  /** AFS grain-fineness multiplier for this mesh (VSync "M3" column). */
  multiplier: number;
  /** Mass of sand retained on this mesh (grams), as entered / read. */
  retainedMass: number;
  /** massᵢ / Σmass · 100. */
  retainedPct: number;
  /** retainedPct × multiplier (VSync "R.M3" column). */
  product: number;
  /** Running Σ retainedPct down the stack (coarse → fine). */
  cumulativePct: number;
  /** 100 − cumulativePct — the "% passing" this mesh. */
  passingPct: number;
}

/** The computed sheet: rows + totals + the Grain Fineness Number. */
export interface SieveSheet {
  rows: SieveSheetRow[];
  /** Σ retained mass (grams). */
  totalMass: number;
  /** GFN = Σ(product)/Σ(retainedPct) — from computeGFN (gfn.ts). */
  gfn: number;
  /** Optional machine/operator-reported fineness for cross-check (VSync gfn_reported). */
  gfnReported?: number;
  /** Σ R.M3 (Σ product) — the GFN numerator. */
  sumProduct: number;
  /** Σ % retained (≈ 100 when every mesh's mass is entered). */
  sumRetainedPct: number;
}

function round(n: number, dp: number): number {
  const f = 10 ** dp;
  return Math.round(n * f) / f;
}

/**
 * Build the full sieve sheet from a sieve set + retained mass per mesh
 * (keyed by `meshDesignation`, e.g. "50" or "pan"). Rows follow the set's
 * `order` (coarse → fine, pan last), so cumulative / passing accumulate down
 * the stack the way a lab sheet reads. Missing / zero masses are valid (a mesh
 * that caught nothing) and simply carry the previous cumulative forward.
 *
 * GFN + per-mesh % come from computeGFN so the number matches the rest of the
 * app (dashboards, capability) exactly.
 */
export function computeSieveSheet(
  set: SieveStep[],
  retainedByMesh: Record<string, number>,
  opts: { gfnReported?: number } = {},
): SieveSheet {
  const ordered = [...set].sort((a, b) => a.order - b.order);
  const { gfn, retainedPct, total } = computeGFN(ordered, retainedByMesh);

  let cumulative = 0;
  let sumProduct = 0;
  let sumRetainedPct = 0;

  const rows: SieveSheetRow[] = ordered.map((step) => {
    const mass = retainedByMesh[step.meshDesignation] ?? 0;
    const pct = retainedPct[step.meshDesignation] ?? 0; // 0 for a zero-mass mesh
    const product = pct * step.afsMultiplier;
    cumulative += pct;
    const passing = Math.max(0, 100 - cumulative);
    sumProduct += product;
    sumRetainedPct += pct;
    const mesh = MESH_BY_APERTURE[step.apertureMicron] ?? {
      astm: step.meshDesignation,
      bss: "-",
    };
    return {
      meshDesignation: step.meshDesignation,
      astm: mesh.astm,
      bss: mesh.bss,
      apertureMicron: step.apertureMicron,
      screenMm: round(step.apertureMicron / 1000, 3),
      multiplier: step.afsMultiplier,
      retainedMass: round(mass, 3),
      retainedPct: round(pct, 2),
      product: round(product, 2),
      cumulativePct: round(cumulative, 2),
      passingPct: round(passing, 2),
    };
  });

  return {
    rows,
    totalMass: round(total, 3),
    gfn: round(gfn, 2),
    gfnReported: opts.gfnReported,
    sumProduct: round(sumProduct, 2),
    sumRetainedPct: round(sumRetainedPct, 2),
  };
}

/** Screen label for a row: "PAN" for the pan, else the aperture in mm. */
export function screenLabel(row: Pick<SieveSheetRow, "apertureMicron" | "screenMm">): string {
  return row.apertureMicron === 0 ? "PAN" : String(row.screenMm);
}

// ── VSync-style tag detection (for the "bind sieve tags" input mode) ─────────

/** What a Thing tag means to the sieve sheet. */
export type SieveTagRole =
  | { kind: "sieve"; mesh: string } // retained-weight tag for a specific mesh
  | { kind: "fineness" } // the machine's reported GFN / AFS number
  | null;

const WEIGHTY = /(ret\.?\s*wt|retain|weight|\bwt\b|mass|\bgm?\b|gram)/i;

/**
 * Map a VSync-style param / tag NAME to its sieve role, mirroring the name
 * grammar GrainSizeController.php accepts:
 *   • "Fineness No." / "AFS No" ................. → the reported GFN
 *   • "1700mic Ret.Wt." / "150 µm weight" ....... → sieve at that aperture
 *   • "Sieve 100 Weight" / "#100 retained" ...... → sieve at that ASTM mesh
 *   • "Pan Weight" .............................. → the pan
 * Returns null when the name isn't a recognisable sieve output, so unrelated
 * tags (temperature, moisture, …) are skipped. `set` defaults to the AFS US
 * series. Pure — the panel decides what to do with the classification.
 */
export function classifySieveTag(name: string, set: SieveStep[] = AFS_US_SERIES): SieveTagRole {
  const s = name.trim();
  if (/fineness|\bafs\s*nos?\b|grain\s*fineness/i.test(s)) return { kind: "fineness" };

  const looksWeighty = WEIGHTY.test(s);
  const mentionsSieve = /sieve|mesh|micron|\bmic\b|µm|\bum\b|#\s*\d|\bpan\b/i.test(s);
  if (!looksWeighty && !mentionsSieve) return null;

  // Pan.
  if (/\bpan\b/i.test(s)) {
    const pan = set.find((x) => x.apertureMicron === 0);
    return pan ? { kind: "sieve", mesh: pan.meshDesignation } : null;
  }

  // Aperture stated in the name (microns).
  const micM = s.match(/(\d{2,4})\s*(?:mic\b|micron|µm|\bum\b)/i);
  if (micM) {
    const mesh = nearestByAperture(set, Number(micM[1]));
    if (mesh) return { kind: "sieve", mesh };
  }

  // ASTM mesh number stated in the name ("sieve 100", "#100", "100 mesh").
  const meshM =
    s.match(/(?:sieve|mesh|#)\s*[:#]?\s*(\d{1,3})\b/i) ?? s.match(/\b(\d{1,3})\s*mesh\b/i);
  if (meshM) {
    const hit = set.find((x) => x.meshDesignation === meshM[1]);
    if (hit) return { kind: "sieve", mesh: hit.meshDesignation };
  }

  return null;
}

/** Closest set mesh to a stated aperture (µm), within 8% — else null. */
function nearestByAperture(set: SieveStep[], micron: number): string | null {
  let best: SieveStep | null = null;
  let bestGap = Infinity;
  for (const step of set) {
    if (step.apertureMicron === 0) continue;
    const gap = Math.abs(step.apertureMicron - micron);
    if (gap < bestGap) {
      bestGap = gap;
      best = step;
    }
  }
  if (!best) return null;
  return bestGap <= best.apertureMicron * 0.08 ? best.meshDesignation : null;
}
